import { useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, LockKeyhole, Mail } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/lib/supabase";
import { Quokka } from "@/components/Quokka";

export function LoginPage() {
  const { session, role, signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (session && role) return <Navigate to="/" replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await signIn(email.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not sign in");
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword() {
    if (!email.trim()) {
      setError("Enter your email first, then request a reset link.");
      return;
    }
    setError(null);
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/login`,
    });
    if (resetError) setError(resetError.message);
    else setNotice("Reset link sent. Check your inbox.");
  }

  return (
    <div className="login">
      <section className="login-brand">
        <div className="login-brand-top">
          <div className="brand" style={{ padding: 0 }}>
            <div className="brand-mark">
              <Quokka size={30} />
            </div>
            <div>
              <div className="brand-name">Flash Quiz</div>
              <div className="brand-sub">Console</div>
            </div>
          </div>
        </div>

        <div className="login-brand-copy">
          <h2>Know what is working before the streak breaks.</h2>
          <p>
            Retention, study behaviour, AI usage, library moderation and push — one
            private console for the people building Flash Quiz.
          </p>
          <div className="login-quokka">
            <Quokka size={150} />
          </div>
        </div>

        <div className="login-brand-foot">
          <span>Staff only</span>
          <span>Access is logged</span>
          <span>© {new Date().getFullYear()} Flash Quiz</span>
        </div>
      </section>

      <section className="login-panel">
        <form className="login-card" onSubmit={submit}>
          <div>
            <h1>Sign in</h1>
            <p className="sub">Use your Flash Quiz staff account.</p>
          </div>

          <div className="field">
            <label htmlFor="email">Email</label>
            <div className="search" style={{ minWidth: 0 }}>
              <Mail size={14} />
              <input
                id="email"
                className="input"
                type="email"
                autoComplete="email"
                placeholder="you@flashquiz.app"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </div>
          </div>

          <div className="field">
            <label htmlFor="password">Password</label>
            <div className="search" style={{ minWidth: 0 }}>
              <LockKeyhole size={14} />
              <input
                id="password"
                className="input"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </div>
          </div>

          {error && <div className="login-error">{error}</div>}
          {notice && <div className="banner banner-info">{notice}</div>}

          <button className="btn btn-primary btn-lg btn-block" disabled={busy}>
            {busy ? "Signing in…" : "Continue"}
            {!busy && <ArrowRight size={16} />}
          </button>

          <button
            type="button"
            className="btn btn-quiet btn-sm"
            onClick={() => void resetPassword()}
          >
            Forgot password?
          </button>

          <p className="login-note">
            Protected by Supabase Auth. Admins should have TOTP enabled.
          </p>
        </form>
      </section>
    </div>
  );
}
